/**
 * Scoped styles for the Profile Activations tab.
 *
 * Ported verbatim from the standalone Favor admin mockup
 * (favor-admin-profile-activations). Every rule is namespaced under `.favor-pa`
 * so the report's own reset / chart styling stays isolated from the admin's
 * global CSS — the same isolation approach used by the Channel Attribution,
 * Messaging Activity and Subscription Report tabs. The admin chrome comes from
 * the surrounding layout, so only the page body is styled here.
 */
const styles = `
.favor-pa{
  --black:#0c0c0e;--orange:#f4521f;--orange-soft:#fff1ec;--gold:#c9a35e;
  --paper:#f6f4f1;--card:#ffffff;--line:#e9e5df;--line-strong:#ddd6cc;
  --txt:#1c1b1a;--txt-2:#6d6a64;--txt-3:#9b978f;
  --green:#1f9d6b;--green-soft:#e7f6ef;--blue:#2f7df6;--blue-soft:#eef4fe;
  --red:#cf4233;--red-soft:#fdecea;--amber:#b07d18;--rose:#b03a63;--rose-soft:#fdeef3;
  /* Deeper than --gold (#c9a35e), which is a fill/accent tone and reads washed-out as a
     2.2px stroke against white. Used for the blended cost line only. */
  --gold-line:#9a7526;
  --serif:"ivypresto-headline",Georgia,serif;
  --sans:"indivisible","Neue Montreal","Helvetica Neue",Arial,sans-serif;
  --tight:"indivisible","Neue Montreal","Helvetica Neue",Arial,sans-serif;
  color:var(--txt);font-family:var(--sans);font-size:14px;line-height:1.5;-webkit-font-smoothing:antialiased;
}
.favor-pa *{margin:0;padding:0;box-sizing:border-box}
.favor-pa .ctx{font-size:12px;color:var(--txt-3);margin:2px 0 14px}
.favor-pa .stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:14px;margin:0 0 20px}
.favor-pa .stat{background:var(--card);border:1px solid var(--line);border-radius:14px;padding:16px 18px;position:relative;overflow:hidden}
.favor-pa .stat .k{font-size:11px;letter-spacing:1.1px;text-transform:uppercase;color:var(--txt-3);font-weight:600}
.favor-pa .stat .v{font-family:var(--serif);font-size:18px;font-weight:700;line-height:1;margin-top:8px;letter-spacing:-.01em}
.favor-pa .stat .d{font-size:12px;color:var(--txt-2);margin-top:3px}
.favor-pa .stat .bar{position:absolute;left:0;top:0;bottom:0;width:4px;background:var(--orange)}
.favor-pa .stat.sg .bar{background:var(--gold)} .favor-pa .stat.sb .bar{background:var(--blue)} .favor-pa .stat.sr .bar{background:var(--rose)}
.favor-pa .delta{display:inline-block;font-size:11px;font-weight:700;padding:2px 7px;border-radius:999px;margin-left:8px;vertical-align:3px}
.favor-pa .delta.up{background:var(--green-soft);color:var(--green)} .favor-pa .delta.dn{background:var(--red-soft);color:var(--red)}
.favor-pa .periods{display:flex;gap:4px;background:var(--card);border:1px solid var(--line);border-radius:11px;padding:4px}
.favor-pa .periods button{border:0;background:transparent;padding:8px 14px;border-radius:8px;font:600 12px var(--sans);color:var(--txt-2);cursor:pointer;white-space:nowrap}
.favor-pa .periods button.on{background:var(--black);color:#fff}
.favor-pa .periods button:focus-visible{outline:2px solid var(--blue)}
.favor-pa .panel{background:var(--card);border:1px solid var(--line);border-radius:14px;padding:22px 24px;margin-bottom:20px}
.favor-pa .phead{display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap}
.favor-pa .phead h2{font-family:var(--serif);font-size:20px;font-weight:600;line-height:1.3}
.favor-pa .pmeta{font-size:11px;letter-spacing:1.6px;color:var(--txt-3);font-weight:700}
.favor-pa .psub{font-size:12.5px;color:var(--txt-3);margin-top:6px}
.favor-pa .legend{display:flex;gap:18px;flex-wrap:wrap;margin-top:12px;font-size:12.5px;color:var(--txt-2);align-items:center}
.favor-pa .legend span{display:inline-flex;align-items:center;gap:6px}
.favor-pa .sw{width:10px;height:10px;border-radius:3px;display:inline-block}
.favor-pa .chartwrap{overflow-x:auto}
.favor-pa .chart{display:flex;gap:8px;align-items:flex-end;height:236px;margin-top:24px}
.favor-pa .chart.g-daily{min-width:1660px}
.favor-pa .chart.g-weekly,.favor-pa .chart.g-monthly{min-width:800px}
.favor-pa .chart.lview{display:block;height:auto;min-width:0;margin-top:18px}
.favor-pa .mcol{flex:1;height:100%;display:flex;flex-direction:column;justify-content:flex-end}
.favor-pa .mcol .mbars{display:flex;align-items:flex-end;justify-content:center;gap:3px;padding:3px 2px 0;border-radius:8px}
.favor-pa .mcol.cur .mbars{outline:2px solid var(--gold);outline-offset:3px}
.favor-pa .gb{position:relative;border-radius:4px 4px 2px 2px}
.favor-pa .g-daily .gb{width:13px}
.favor-pa .g-weekly .gb,.favor-pa .g-monthly .gb{width:24px}
.favor-pa .gb .cnt{position:absolute;top:-15px;left:-12px;right:-12px;text-align:center;font-size:8.5px;font-weight:700;white-space:nowrap}
.favor-pa .gb.m{background:var(--blue)} .favor-pa .gb.m .cnt{color:var(--blue)}
.favor-pa .gb.f{background:var(--rose)} .favor-pa .gb.f .cnt{color:var(--rose)}
.favor-pa .mlab{text-align:center;font-size:10.5px;font-weight:600;color:var(--txt-3);margin-top:8px;height:26px;line-height:1.25}
.favor-pa .mlab small{display:block;font-size:9px;color:var(--txt-3);font-weight:600}
.favor-pa .totline{font-size:12.5px;color:var(--txt-2);margin-top:14px;border-top:1px solid var(--line);padding-top:12px}
.favor-pa .totline b{font-variant-numeric:tabular-nums}
/* Unallocated-spend caveat under the cost panel. Amber, not red: it is a disclosure about
   campaign naming, not an error — nothing is broken when it shows. */
.favor-pa .totline.warn{color:var(--amber);border-top:0;margin-top:6px;padding-top:0}
.favor-pa .tblwrap{overflow-x:auto}
/* Cohort grid is 60 columns wide - far wider than the content column - so it gets the
   CONTAINED scroll box, never page-sticky, or it forces a scrollbar onto the whole page.
   border-collapse:separate is deliberate: under collapse, borders vanish on sticky cells. */
.favor-pa .cwrap{max-height:620px;overflow:auto;margin-top:16px;border:1px solid var(--line);border-radius:10px}
.favor-pa .ctbl{border-collapse:separate;border-spacing:0;font-size:11.5px;width:max-content}
.favor-pa .ctbl thead th{position:sticky;top:0;background:var(--card);z-index:3}
.favor-pa .ctbl th.rh,.favor-pa .ctbl td.rh{position:sticky;left:0;background:var(--card);z-index:2}
.favor-pa .ctbl thead th.rh{z-index:4}
.favor-pa .ctbl tr.avg td{position:sticky;bottom:0;background:var(--card);z-index:3}
.favor-pa .ctbl tr.avg td.rh{z-index:4}
.favor-pa .ctbl th{font-size:10px;letter-spacing:1px;text-transform:uppercase;color:var(--txt-3);font-weight:700;padding:7px 8px;border-bottom:1px solid var(--line-strong);text-align:center}
.favor-pa .ctbl th.rh{text-align:left}
.favor-pa .ctbl td{padding:7px 6px;text-align:center;border-bottom:1px solid var(--line);font-variant-numeric:tabular-nums;min-width:44px}
.favor-pa .ctbl td.rh{text-align:left;white-space:nowrap;font-weight:600}
.favor-pa .ctbl td.rh small{color:var(--txt-3);font-weight:600;margin-left:6px}
.favor-pa .ctbl td.d0{background:#f1efe9;font-weight:700}
.favor-pa .ctbl tr.avg td{border-top:2px solid var(--line-strong);border-bottom:0;font-weight:700}
.favor-pa .ctbl td.ck{cursor:pointer}
.favor-pa .ctbl td.ck:hover{outline:2px solid var(--blue);outline-offset:-2px}
.favor-pa .cdetail{margin-top:12px;font-size:13px;background:#faf8f5;border:1px solid var(--line);border-radius:10px;padding:11px 14px}
.favor-pa .cdetail b{font-weight:800}
.favor-pa .cdetail .cg{color:var(--green);font-weight:800}
.favor-pa .cdetail .cr{color:var(--red);font-weight:800}
.favor-pa .cdetail .cb{color:var(--blue);font-weight:800}
.favor-pa .verdict{margin-top:12px;font-size:13.5px;background:var(--blue-soft);border:1px solid #d8e6fb;border-radius:10px;padding:11px 14px}
.favor-pa .verdict b{font-weight:800}
.favor-pa .atbl{border-collapse:collapse;margin-top:16px;font-size:12.5px;min-width:640px;width:100%}
.favor-pa .atbl th{font-size:10px;letter-spacing:1px;text-transform:uppercase;color:var(--txt-3);font-weight:700;padding:7px 10px;border-bottom:1px solid var(--line-strong);text-align:right}
.favor-pa .atbl th:first-child{text-align:left}
.favor-pa .atbl td{padding:9px 10px;border-bottom:1px solid var(--line);text-align:right;font-variant-numeric:tabular-nums}
.favor-pa .atbl td:first-child{text-align:left;font-weight:700}
.favor-pa .atbl tr:last-child td{border-bottom:0}
.favor-pa .atbl .dm{color:var(--blue)}
.favor-pa .atbl .df{color:var(--rose)}
.favor-pa .atbl tr.dif td{border-top:2px solid var(--line-strong);color:var(--green);font-weight:700}
.favor-pa .atbl tr.dif td:first-child{color:var(--txt)}
`;

export default styles;
